/**
 * IMPORTS
 */

import { useEffect, useState } from "react";
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from "react-router-dom";

import { Menu } from "lucide-react";

import { FeatureRoute } from "@components/feature-route/feature-route";
// Modal de migração desligado até todos migrarem; reativar junto com o bloco no AuthenticatedLayout
// import { ModalMigracaoSistema } from "@components/modal-migracao-sistema/modal-migracao-sistema";
import { MonitorSomNotificacoes } from "@components/notificacoes/monitor-som-notificacoes";
import { PaginaNaoEncontrada, RouteError } from "@components/route-error/route-error";
// components-main
import { Sidebar } from "@components/sidbar/sidbar";
import MobileMenu from "@components/sidebar-mobile/sidebar-mobile";

// admin + producao
// import { BackupsPage } from "@pages/admin/backups";
import { DetailsClient } from "@pages/admin/detalhes-cliente";
import { EditarDadosCliente } from "@pages/admin/editar-dados-cliente";
import { ManageClient } from "@pages/admin/gerenciar-clientes";
import { ManageCredits } from "@pages/admin/gerenciar-creditos";
import { ManageTechnicalSheets } from "@pages/admin/gerenciar-modelos-tecnicos/manage-technical-sheets";
import { ManageClientUsers } from "@pages/admin/gerenciar-usuarios-cliente/manage-client-users";
import { MyData } from "@pages/admin/meus-dados/my-data";
import { SearchShipment } from "@pages/admin/pesquisar-remessas";
import { PegarTarefasAdminPage } from "@pages/admin/remessas";
import { VisualizarModelo } from "@pages/admin/visualizar-modelo";
// cliente
import { AgendaClientePage } from "@pages/client/agenda";
import { ConversaClientePage } from "@pages/client/conversa";
import { ModelPage } from "@pages/client/dashboard";
import { DetailsModelPage } from "@pages/client/details-model";
import { DetailsShipmentInProgress } from "@pages/client/details-shipment-in-progress";
import { MyDataPages } from "@pages/client/my-data";
import { NotificacoesPage } from "@pages/client/notificacoes";
import { RequestShipmentLoosePage } from "@pages/client/request-shipment-loose";
import { ShippingSearchRequestPages } from "@pages/client/search-requests";
import { ShippingInProgressPages } from "@pages/client/shipment-in-progress";
import { ShippingHistoryPage } from "@pages/client/shipping-history";
import { DashboardExpedicao } from "@pages/expedicao/dashboard";
import { MyDataExpedicao } from "@pages/expedicao/meus-dados";
import { DashboardRecepcao } from "@pages/recepcao/dashboard";
import { MyDataRecepcao } from "@pages/recepcao/meus-dados";
import { RecoverPassword } from "@pages/recover-password";
import { ResetPasswordConfirm } from "@pages/reset-password";
// pages
import { SignIn } from "@pages/signin";

import { useSincronizarDadosUsuario } from "@/hooks/dados-usuario";

// hooks / zustand
import { useStoreZustandUserAuth } from "@/store-zustand/user-auth";

import { resolveUserProfile } from "@/common/navigation/user-profile";
import { FEATURES, type FeatureKey } from "@/utils/auth/features";

// Features exigidas por tela: a rota só abre quando o usuário tem pelo menos uma delas.
// Os grupos abaixo usam as features de ACESSO (ACESSAR_*) quando a rota é a tela
// principal de um item do menu lateral — essas features controlam só a navegação,
// nunca uma ação específica dentro da tela (isso é papel das features de
// funcionalidade, como editarCliente/creditos/usuariosCliente/modelos).
const FEATURES_POR_ROTA = {
  // "tarefas" segue com a feature de funcionalidade porque também é usada em
  // /search-requests (cliente), que não é a tela de tarefas do admin/produção.
  tarefas: [FEATURES.PEGAR_TAREFA_REMESSAS, FEATURES.MINHAS_TAREFA_REMESSAS],
  acessarRemessas: [FEATURES.ACESSAR_REMESSAS],
  expedicao: [FEATURES.ACESSAR_EXPEDICAO],
  recepcao: [FEATURES.ACESSAR_RECEPCAO],
  pesquisar: [FEATURES.ACESSAR_PESQUISAR_REMESSAS],
  clientes: [FEATURES.ACESSAR_GERENCIAR_CLIENTE],
  editarCliente: [FEATURES.EDITAR_CLIENTE],
  creditos: [FEATURES.GERENCIAR_CREDITOS_CLIENTE],
  usuariosCliente: [FEATURES.VISUALIZAR_USUARIO_CLIENTE],
  modelos: [FEATURES.VISUALIZAR_MODELOS_TECNICOS],
  visualizarModelo: [
    FEATURES.VISUALIZAR_MODELOS_TECNICOS,
    FEATURES.PESQUISAR_REMESSAS,
    FEATURES.PEGAR_TAREFA_REMESSAS,
    FEATURES.MINHAS_TAREFA_REMESSAS,
    FEATURES.LIBERACAO_REMESSA_BALCAO,
    FEATURES.LIBERACAO_MULTIPLAS_REMESSAS_BALCAO,
    FEATURES.LIBERACAO_REMESSAS_RECEPCAO,
    FEATURES.LIBERACAO_MULTIPLAS_REMESSAS_RECEPCAO,
  ],
  // cliente
  modelosCliente: [FEATURES.ACESSAR_MODELOS_CLIENTE],
  solicitarRemessa: [FEATURES.SOLICITAR_REMESSAS],
  remessasAndamento: [FEATURES.ACESSAR_REMESSAS_ANDAMENTO],
  historicoRemessas: [FEATURES.ACESSAR_HISTORICO_REMESSAS],
  conversaCliente: [FEATURES.ACESSAR_CONVERSA_CLIENTE],
  agendaCliente: [FEATURES.ACESSAR_AGENDA_CLIENTE],
  notificacoes: [FEATURES.ACESSAR_NOTIFICACOES],
  detalhesRemessaCliente: [
    FEATURES.VISUALIZAR_REMESSAS_ANDAMENTO,
    FEATURES.VISUALIZAR_HISTORICO_REMESSAS,
  ],
} as const satisfies Record<string, readonly FeatureKey[]>;

// Layout para as rotas autenticadas
function AuthenticatedLayout() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // Desligado até todos migrarem. Ao reativar (descomentar este bloco, o import do
  // ModalMigracaoSistema e o useFeature), com exibir_banner_manutencao ligada o sistema
  // fica bloqueado: só renderiza o modal que leva para o sistema migrado.
  // const sistemaMigrado = useFeature(FEATURES.EXIBIR_BANNER_MANUTENCAO);
  //
  // if (sistemaMigrado) return <ModalMigracaoSistema />;

  return (
    <div className="flex h-screen overflow-hidden bg-gray-custom900">
      {/* som de notificação nova em qualquer tela (só com acessar_notificacoes) */}
      <MonitorSomNotificacoes />
      {/* Sidebar para telas maiores */}
      <div className="hidden min-h-screen md:block">
        <Sidebar />
      </div>

      {/* Menu Mobile */}
      <div className="fixed left-0 top-0 flex w-full items-center justify-between bg-gray-custom900 p-4 md:hidden">
        {/* Botão de abrir o menu */}
        <Menu
          className="cursor-pointer text-2xl text-white"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        />
      </div>

      {/* Overlay + drawer do menu mobile */}
      {isMobileMenuOpen && (
        <>
          <button
            type="button"
            aria-label="Fechar menu"
            className="fixed inset-0 z-40 bg-black/50 md:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div className="fixed left-0 top-0 z-50 h-full w-64 md:hidden">
            <MobileMenu closeMenu={() => setIsMobileMenuOpen(false)} isOpen={isMobileMenuOpen} />
          </div>
        </>
      )}

      <div className="mt-12 flex min-h-0 min-w-0 flex-1 flex-col md:mt-0">
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-background p-4">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

// Rotas não autenticadas
const routerOpen = createBrowserRouter([
  {
    path: "/",
    element: <SignIn />,
    errorElement: <RouteError />,
  },
  {
    path: "/recover-password",
    element: <RecoverPassword />,
  },
  {
    path: "/reset-password-confirm",
    element: <ResetPasswordConfirm />,
  },
  { path: "*", element: <Navigate to="/" replace />, errorElement: <RouteError /> },
]);

// Rotas autenticadas (Admin)
const adminRouterAuth = createBrowserRouter([
  {
    path: "/",
    element: <AuthenticatedLayout />,
    errorElement: <RouteError />,
    children: [
      {
        path: "/",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.acessarRemessas}>
            <PegarTarefasAdminPage />
          </FeatureRoute>
        ),
      },
      {
        path: "/expedition",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.expedicao}>
            <DashboardExpedicao />
          </FeatureRoute>
        ),
      },
      {
        path: "/reception",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.recepcao}>
            <DashboardRecepcao />
          </FeatureRoute>
        ),
      },
      {
        path: "/manage-clients",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.clientes}>
            <ManageClient />
          </FeatureRoute>
        ),
      },
      {
        path: "edit-client-data",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.editarCliente}>
            <EditarDadosCliente />
          </FeatureRoute>
        ),
      },
      {
        path: "/search-shipment",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.pesquisar}>
            <SearchShipment />
          </FeatureRoute>
        ),
      },
      {
        path: "/manage-credits",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.creditos}>
            <ManageCredits />
          </FeatureRoute>
        ),
      },
      {
        path: "/details-client",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.clientes}>
            <DetailsClient />
          </FeatureRoute>
        ),
      },
      {
        path: "/my-data",
        element: <MyData />,
      },
      {
        path: "/manage-client-users",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.usuariosCliente}>
            <ManageClientUsers />
          </FeatureRoute>
        ),
      },
      {
        path: "visualizar-modelo",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.visualizarModelo}>
            <VisualizarModelo />
          </FeatureRoute>
        ),
      },
      {
        path: "/manage-technical-sheets",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.modelos}>
            <ManageTechnicalSheets />
          </FeatureRoute>
        ),
      },
      { path: "*", element: <PaginaNaoEncontrada /> },
    ],
  },
]);

// Rotas autenticadas (Clientes)
const clientRouterAuth = createBrowserRouter([
  {
    path: "/",
    element: <AuthenticatedLayout />,
    errorElement: <RouteError />,
    children: [
      {
        path: "/",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.modelosCliente}>
            <ModelPage />
          </FeatureRoute>
        ),
      },
      {
        path: "/details-model",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.solicitarRemessa}>
            <DetailsModelPage />
          </FeatureRoute>
        ),
      },
      {
        path: "/shipments-in-progress",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.remessasAndamento}>
            <ShippingInProgressPages />
          </FeatureRoute>
        ),
      },
      {
        path: "/details-in-progress",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.detalhesRemessaCliente}>
            <DetailsShipmentInProgress />
          </FeatureRoute>
        ),
      },
      {
        path: "/search-requests",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.tarefas}>
            <ShippingSearchRequestPages />
          </FeatureRoute>
        ),
      },
      {
        path: "/my-data",
        element: <MyDataPages />,
      },
      {
        path: "/shipping-history",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.historicoRemessas}>
            <ShippingHistoryPage />
          </FeatureRoute>
        ),
      },
      {
        path: "/request-shipment-loose",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.solicitarRemessa}>
            <RequestShipmentLoosePage />
          </FeatureRoute>
        ),
      },
      {
        // mesmo caminho que a API grava no link das notificações do cliente
        path: "/minha-conversa",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.conversaCliente}>
            <ConversaClientePage />
          </FeatureRoute>
        ),
      },
      {
        path: "/minha-agenda",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.agendaCliente}>
            <AgendaClientePage />
          </FeatureRoute>
        ),
      },
      {
        path: "/notificacoes",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.notificacoes}>
            <NotificacoesPage />
          </FeatureRoute>
        ),
      },
      { path: "*", element: <PaginaNaoEncontrada /> },
    ],
  },
]);

// Produção
const producaoRouterAuth = createBrowserRouter([
  {
    path: "/",
    element: <AuthenticatedLayout />,
    errorElement: <RouteError />,
    children: [
      {
        path: "/",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.acessarRemessas}>
            <PegarTarefasAdminPage />
          </FeatureRoute>
        ),
      },
      {
        path: "manage-clients",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.clientes}>
            <ManageClient />
          </FeatureRoute>
        ),
      },
      {
        path: "search-shipment",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.pesquisar}>
            <SearchShipment />
          </FeatureRoute>
        ),
      },
      { path: "my-data", element: <MyData /> },
      {
        path: "manage-client-users",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.usuariosCliente}>
            <ManageClientUsers />
          </FeatureRoute>
        ),
      },
      {
        path: "manage-technical-sheets",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.modelos}>
            <ManageTechnicalSheets />
          </FeatureRoute>
        ),
      },
      {
        path: "edit-client-data",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.editarCliente}>
            <EditarDadosCliente />
          </FeatureRoute>
        ),
      },
      {
        path: "visualizar-modelo",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.visualizarModelo}>
            <VisualizarModelo />
          </FeatureRoute>
        ),
      },
      {
        path: "/manage-credits",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.creditos}>
            <ManageCredits />
          </FeatureRoute>
        ),
      },
      {
        path: "/details-client",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.clientes}>
            <DetailsClient />
          </FeatureRoute>
        ),
      },
      { path: "*", element: <PaginaNaoEncontrada /> },
    ],
  },
]);

// Consultor
const consultorRouterAuth = createBrowserRouter([
  {
    path: "/",
    element: <AuthenticatedLayout />,
    errorElement: <RouteError />,
    children: [
      {
        path: "/",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.pesquisar}>
            <SearchShipment />
          </FeatureRoute>
        ),
      },
      {
        path: "manage-clients",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.clientes}>
            <ManageClient />
          </FeatureRoute>
        ),
      },
      { path: "my-data", element: <MyData /> },
      {
        path: "manage-client-users",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.usuariosCliente}>
            <ManageClientUsers />
          </FeatureRoute>
        ),
      },
      {
        path: "edit-client-data",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.editarCliente}>
            <EditarDadosCliente />
          </FeatureRoute>
        ),
      },
      {
        path: "manage-technical-sheets",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.modelos}>
            <ManageTechnicalSheets />
          </FeatureRoute>
        ),
      },
      {
        path: "/manage-credits",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.creditos}>
            <ManageCredits />
          </FeatureRoute>
        ),
      },
      {
        path: "/details-client",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.clientes}>
            <DetailsClient />
          </FeatureRoute>
        ),
      },
      { path: "*", element: <PaginaNaoEncontrada /> },
    ],
  },
]);

// Expedição
const expedicaoRouterAuth = createBrowserRouter([
  {
    path: "/",
    element: <AuthenticatedLayout />,
    errorElement: <RouteError />,
    children: [
      {
        path: "/",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.expedicao}>
            <DashboardExpedicao />
          </FeatureRoute>
        ),
      },
      {
        path: "visualizar-modelo",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.visualizarModelo}>
            <VisualizarModelo />
          </FeatureRoute>
        ),
      },
      {
        path: "search-shipment",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.pesquisar}>
            <SearchShipment />
          </FeatureRoute>
        ),
      },
      { path: "my-data", element: <MyDataExpedicao /> },
      { path: "*", element: <PaginaNaoEncontrada /> },
    ],
  },
]);

// Recepção
const recepcaoRouterAuth = createBrowserRouter([
  {
    path: "/",
    element: <AuthenticatedLayout />,
    errorElement: <RouteError />,
    children: [
      {
        path: "/",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.recepcao}>
            <DashboardRecepcao />
          </FeatureRoute>
        ),
      },
      {
        path: "visualizar-modelo",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.visualizarModelo}>
            <VisualizarModelo />
          </FeatureRoute>
        ),
      },
      {
        path: "/search-shipment",
        element: (
          <FeatureRoute features={FEATURES_POR_ROTA.pesquisar}>
            <SearchShipment />
          </FeatureRoute>
        ),
      },
      { path: "my-data", element: <MyDataRecepcao /> },
      { path: "*", element: <PaginaNaoEncontrada /> },
    ],
  },
]);

function AppRoutes() {
  const { isAuthenticated, getUserIsAuthenticated, features } = useStoreZustandUserAuth();

  useSincronizarDadosUsuario();

  const getRouterByFeatures = () => {
    const profile = resolveUserProfile(features);

    switch (profile) {
      case "admin":
        return adminRouterAuth;
      case "consultor":
        return consultorRouterAuth;
      case "cliente":
        return clientRouterAuth;
      case "producao":
        return producaoRouterAuth;
      case "expedicao":
        return expedicaoRouterAuth;
      case "recepcao":
        return recepcaoRouterAuth;
      default:
        return routerOpen;
    }
  };

  useEffect(() => {
    getUserIsAuthenticated();
  }, [getUserIsAuthenticated]);

  return (
    <div>
      <RouterProvider router={isAuthenticated ? getRouterByFeatures() : routerOpen} />
    </div>
  );
}

/**
 * EXPORTS
 */
export { AppRoutes };
